
import { TextField, Button, FormControl, Autocomplete, RadioGroup, FormControlLabel, Radio, InputLabel } from "@mui/material";
import React, { useState } from "react";
import { Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { Role } from "../../../constants/Role";
import { useSelector } from "react-redux";
import { WorkshopType } from "../../../constants/Status";
import Drawer from "@mui/material/Drawer";
import CloseIcon from '@mui/icons-material/Close';
import { withTranslation } from "react-i18next";
import { Action } from "../../../constants/Common";
import SweetAlert from "react-bootstrap-sweetalert";
import { generateTimes } from "../../../helpers/string_helper";
import SearchDropDownListPaginationComponent from "../common/SearchDropDownPaginationList"
import { getAllPortfolios } from "../../../api/casp/portfolio";

const CreateWorkshop = ({ open, onClose, action, selectedRow, handleSubmit, t }) => {
    const normalizedPortfolio = selectedRow?.portfolio
    ? {
        id: selectedRow.portfolio.id,
        label: selectedRow.portfolio.name,
        key: `${selectedRow.portfolio.id}|${selectedRow.portfolio.name}`,
    }
    : null;
    
    const loggedInUser = useSelector((state) => state.Profile.user);
    const workshopInitial = {
        date: selectedRow?.date ? dayjs(selectedRow?.date) : "",
        name: selectedRow?.name ?? "",
        location: selectedRow?.location ?? "",
        startTime: selectedRow?.startTime ?? "",
        endTime: selectedRow?.endTime ?? "",
        details: selectedRow?.details ?? "",
        type: selectedRow?.type ?? "Workshop",
        isAttendance: selectedRow?.isAttendance ?? 'false',
        portfolio: normalizedPortfolio
    }
    const [portfolio, setPortfolio] = useState(normalizedPortfolio);
    const [agendaCreationLoading, setAgendaCreationLoading] = useState(false);
    const [isOpenSweetAlert, setIsOpenSweetAlert] = useState(false);

    const isDisabled = loggedInUser?.role?.name !== Role.EPMO_OFFICER || action === Action.VIEW

    const workshopValidation = Yup.object().shape({
        date: Yup.date()
            .typeError(t("Given date is invalid type"))
            .min(dayjs().subtract(1, 'day'), "Event date must be greater or equal to current date")
            .required(t("Please Enter event date")),
        type: Yup.string()
            .strict(true)
            .trim(t('Must not include leading and trailing spaces'))
            .required(t("Please select workshop type")),
        name: Yup.string()
            .max(250, t("Too Long!"))
            .strict(true)
            .trim(t('Must not include leading and trailing spaces'))
            .required(t("Please enter workshop name")),
        isAttendance: Yup.string().required(t("Please select radio button for attendance is optional or mandatory")),
        location: Yup.string()
            .max(250, t("Too Long!"))
            .strict(true)
            .trim(t('Must not include leading and trailing spaces'))
            .required(t("Please Enter workshop location")),
        startTime: Yup.string()
            .max(250, t("Too Long!"))
            .strict(true)
            .trim(t('Must not include leading and trailing spaces'))
            .required(t("Please select start time")),
        endTime: Yup.string()
            .max(250, t("Too Long!"))
            .strict(true)
            .trim(t('Must not include leading and trailing spaces'))
            .required(t("Please select end time")),
        details: Yup.string()
            .max(250, t("Too Long!"))
            .strict(true)
            .trim(t('Must not include leading and trailing spaces'))
            .optional(),
        portfolio: Yup.object()
            .nullable()
            .required(t("Please select portfolio")),
    });

    const formik = useFormik({
        initialValues: {
            ...workshopInitial,
        },
        validationSchema: workshopValidation,
        validateOnMount: true,
    });

    const handleStartChange = (value) => {
        formik.setFieldValue("startTime", value ? value : "")
        if (formik.values.endTime && value >= formik.values.endTime) {
            formik.setFieldValue("endTime", "")
        }
    };

    const times = generateTimes(formik.values.date);

    const filteredEndTimes = times.filter((time) => !formik.values.startTime || time > formik.values.startTime);

    const isDisabledField = agendaCreationLoading || isDisabled;

    const renderTextWithLinks = (text) => {
      const urlRegex = /((https?:\/\/)[\w-]+(\.[\w-]+)+([\w\-._~:/?#[\]@!$&'()*+,;=]*)?)/gi;

      const htmlWithLinks = text.replace(urlRegex, (url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${url}</a>`;
      });

      return htmlWithLinks
    };
    
    return (
        <React.Fragment>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: "580px" },
                }}
                className="projectListDrawer addRiskDrawer"
            >
                <form className="p-3">
                    <h5 className="border-bottom-0">
                        {t(`${action && isDisabled ? t(Action.VIEW) : t(action)} ${formik?.values?.type ? t(formik.values?.type) : "Workshop"}`)}
                    </h5>
                    <CloseIcon className="crossIcon" onClick={onClose} />
                    <div className="innerTbContent">
                        <div className="mb-3 plDatepicker">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={["DatePicker"]}>
                                    <DatePicker
                                        label={t("Workshop Date") + ' *'}
                                        className="form-control"
                                        disabled={agendaCreationLoading || isDisabled}
                                        format="MM/DD/YYYY"
                                        name="date"
                                        minDate={action === Action.VIEW ? "" : dayjs(new Date())}
                                        value={
                                            formik?.values?.date
                                                ? formik.values.date
                                                : null
                                        }
                                        on
                                        slotProps={{
                                            field: {
                                                clearable: true,
                                                onBlur: (_) => {
                                                    if (!formik.touched?.date) {
                                                        formik.setFieldTouched("date", true, false);
                                                    }
                                                },
                                            },
                                            onClear: () => {
                                                formik.setFieldValue("date", "");
                                            },
                                        }}
                                        onChange={(e) => {
                                            formik.setFieldValue("date", e ? dayjs(e) : "");
                                            if (!formik.touched?.date) {
                                                formik.setFieldTouched("date", true, false);
                                            }
                                        }}
                                        required
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                            {formik.touched.date && formik.errors.date && (
                                <span className="text-danger">{formik.errors.date}</span>
                            )}
                        </div>
                        <div className="mb-3">
                            <Autocomplete
                                name="type"
                                value={formik.values?.type}
                                size="small"
                                options={WorkshopType.map(type => type)}
                                getOptionLabel={(option) => t(option)}
                                disabled={agendaCreationLoading || isDisabled}
                                onChange={(_, value) => formik.setFieldValue("type", value ?? "")}
                                onBlur={() => {
                                    if (!formik.touched?.type) {
                                        formik.setFieldTouched("type", true, true);
                                    }
                                }}
                                renderInput={(params) => <TextField {...params}
                                    label={t("Workshop Type") + " *"}
                                    size="small"
                                />}
                            />
                            {formik.touched.type && formik.errors.type && (
                                <span className="text-danger d-block">{formik.errors.type}</span>
                            )}
                        </div>
                        <div className="mb-3">
                            <TextField
                                id="outlined-basic"
                                label={t(`${formik.values?.type} Name`)}
                                name="name"
                                disabled={agendaCreationLoading || isDisabled}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                fullWidth
                                required
                            />
                            {formik.touched.name && formik.errors.name && (
                                <span className="text-danger">{formik.errors.name}</span>
                            )}
                        </div>

                        <div className="me-2">
                            <SearchDropDownListPaginationComponent
                                label={t("Cohort *")}
                                sx={{
                                width: 350,
                                }}
                                searchEnabled={false}
                                disabled={agendaCreationLoading || isDisabled}
                                listCall={getAllPortfolios}
                                disableClearable={!portfolio}
                                defaultValue={formik.values.portfolio}
                                apiParams={{
                                    order: "updatedAt",
                                    direction: "desc",
                                    ...(![
                                        Role.PROJECT_MANAGER,
                                        Role.EPMO_MANAGER,
                                        Role.ROOT_ADMIN,
                                        Role.VICE_PRESIDENT,
                                        Role.PRESIDENT,
                                        Role.PROGRAM_MANAGER,
                                        Role.EPMO_OFFICER,
                                        Role.MENTOR
                                    ].includes(loggedInUser?.role?.name) && {
                                        filterFields: "portfolioUser",
                                        filterInputs: loggedInUser.id,
                                    }),
                                }}
                                emitItem={(newValue) => {
                                setPortfolio(newValue);
                                formik.setFieldValue('portfolio', newValue)
                                formik.setFieldTouched("portfolio", true, false);
                                }}
                            />
                            {formik.touched.portfolio && formik.errors.portfolio && (
                                <span className="text-danger d-block">{formik.errors.portfolio}</span>
                            )}
                        </div>
                        <div className="mb-3">
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    name="isAttendance"
                                    value={formik.values.isAttendance}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <FormControlLabel value='true' disabled={agendaCreationLoading || isDisabled} control={<Radio />} label={t("Attendance Mandatory")} />
                                    <FormControlLabel value='false' disabled={agendaCreationLoading || isDisabled} control={<Radio />} label={t("Attendance Optional")} />
                                </RadioGroup>
                            </FormControl>
                        </div>

                        <div className="mb-3">
                            <TextField
                                id="outlined-basic"
                                label={t("Workshop Location")}
                                name="location"
                                disabled={agendaCreationLoading || isDisabled}
                                value={formik.values.location}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                fullWidth
                                required
                            />
                            {formik.touched.location && formik.errors.location && (
                                <span className="text-danger">{formik.errors.location}</span>
                            )}
                        </div>
                        <div className="row mb-3">
                            <div className="col-sm-6 col-md-6">
                                <Autocomplete
                                    name="startTime"
                                    value={formik.values.startTime}
                                    size="small"
                                    options={times}
                                    disabled={agendaCreationLoading || isDisabled}
                                    onChange={(_, value) => {
                                        handleStartChange(value)
                                    }}
                                    onBlur={() => {
                                        if (!formik.touched?.startTime) {
                                            formik.setFieldTouched("startTime", true, true);
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params}
                                        label={t("Start Time") + " *"}
                                        size="small"
                                    />}
                                />
                                {formik.touched.startTime && formik.errors.startTime && (
                                    <span className="text-danger">{formik.errors.startTime}</span>
                                )}
                            </div>
                            <div className="col-sm-6 col-md-6">
                                <Autocomplete
                                    name="endTime"
                                    value={formik.values.endTime}
                                    size="small"
                                    options={filteredEndTimes}
                                    disabled={agendaCreationLoading || isDisabled}
                                    onChange={(_, value) => {
                                        formik.setFieldValue("endTime", value ? value : "")
                                    }}
                                    onBlur={() => {
                                        if (!formik.touched?.endTime) {
                                            formik.setFieldTouched("endTime", true, true);
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params}
                                        label={t("End Time") + " *"}
                                        size="small"
                                    />}
                                />
                                {formik.touched.endTime && formik.errors.endTime && (
                                    <span className="text-danger">{formik.errors.endTime}</span>
                                )}
                            </div>
                        </div>
                        {/* <div className="mb-3">
                            <TextField
                                id="outlined-basic"
                                label={t("Details")}
                                name="details"
                                disabled={agendaCreationLoading || isDisabled}
                                value={formik.values.details}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                fullWidth
                            />
                            {formik.touched.details && formik.errors.details && (
                                <span className="text-danger">{formik.errors.details}</span>
                            )}
                        </div> */}

                        <div className="mb-3">
                        {isDisabledField ? (

                            <div style={{ marginBottom: "8px" }}>
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "4px",
                                  fontSize: 12,
                                  color: "rgba(0, 0, 0, 0.6)",
                                }}
                              >
                                Details
                              </label>

                              <div
                                style={{
                                  border: "1px solid rgba(0, 0, 0, 0.23)",
                                  borderRadius: 4,
                                  padding: "16.5px 14px",
                                  minHeight: "56px",
                                  backgroundColor: "#f5f5f5",
                                  fontSize: "16px",
                                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                  color: "rgba(0, 0, 0, 0.6)",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                                dangerouslySetInnerHTML={{ __html: renderTextWithLinks(formik.values.details) }}
                              />
                            </div>
                          ) : (
                            <TextField
                            id="outlined-basic"
                            label={t("Details")}
                            name="details"
                            disabled={isDisabledField}
                            value={formik.values.details}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                            />
                        )}

                        {formik.touched.details && formik.errors.details && (
                            <span className="text-danger">{formik.errors.details}</span>
                        )}
                        </div>


                    </div>
                    {loggedInUser?.role?.name === Role.EPMO_OFFICER && Action.VIEW !== action && (
                        <div className="plsubmitButton">
                            <Button
                                className="submitBtn btn addNew_ProjectBtn mx-2"
                                onClick={() => handleSubmit(formik.values)}
                                variant="contained"
                                color="success"
                                disabled={(action === Action.EDIT && !formik.dirty) || agendaCreationLoading || !formik.isValid}
                                sx={{
                                    cursor: agendaCreationLoading || !formik.isValid ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {agendaCreationLoading && (
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-1"
                                    />
                                )}
                                <span className="visually-hidden">Loading...</span>
                                <span style={{ textTransform: 'none' }}>{action === Action.CREATE ? t('Submit') : t('Update')}</span>
                            </Button>
                            {action === Action.EDIT && (
                                <Button
                                    className="submitBtn btn addNew_ProjectBtn"
                                    onClick={() => setIsOpenSweetAlert(true)}
                                    variant="contained"
                                    color="error"
                                    disabled={agendaCreationLoading || !formik.isValid}
                                    sx={{
                                        cursor: agendaCreationLoading || !formik.isValid ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {agendaCreationLoading && (
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-1"
                                        />
                                    )}
                                    <span className="visually-hidden">Loading...</span>
                                    <span style={{ textTransform: 'none' }}>{t('Remove')}</span>
                                </Button>
                            )}
                        </div>
                    )}
                </form>
            </Drawer>

            {isOpenSweetAlert && (
                <SweetAlert
                    title={t(`Are you sure want to delete this ${selectedRow?.type}`) + "?"}
                    warning
                    showCancel
                    confirmBtnText={t("OK")}
                    cancelBtnText={t("Cancel")}
                    confirmBtnBsStyle="success"
                    cancelBtnBsStyle="danger"
                    onConfirm={async () => {
                        handleSubmit(Action.DELETE);
                    }}
                    onCancel={() => {
                        setIsOpenSweetAlert(false);
                    }}
                ></SweetAlert>
            )}
        </React.Fragment>
    );
}

export default withTranslation()(CreateWorkshop)
